/**
 * apps/battle/client/src/admin/AdminApp.tsx — Admin console shell.
 *
 * Mounted by main.tsx when window.location.pathname starts with /admin AND
 * the shared battle session reports role === 'admin'. Pre-auth + wrong-role
 * gating happen in the parent (AdminRoot), so this component assumes a valid
 * admin session.
 *
 * Layout mirrors ReviewApp: topbar + left rail + detail pane. Three screens:
 *   - Users   → list / create / edit / reset / deactivate
 *   - Knobs   → system_config rows (Runtime, Grading Model, Prompts sub-tabs)
 *   - Health  → DB ping, user counts, failed gradings, LLM probe button
 */
import { useState } from 'react';
import './styles.css';
import { UsersScreen } from './UsersScreen.js';
import { KnobsScreen } from './KnobsScreen.js';
import { HealthScreen } from './HealthScreen.js';

export interface AdminAppProps {
  adminName: string;
  adminId: string;
  onLogout: () => Promise<void> | void;
  onExitToGame: () => void;
}

type Screen = 'users' | 'knobs' | 'health';

export function AdminApp({ adminName, adminId, onLogout, onExitToGame }: AdminAppProps) {
  const [screen, setScreen] = useState<Screen>('users');
  const [toast, setToast] = useState<{ msg: string; kind: 'success' | 'error' | 'warn' } | null>(null);

  function showToast(msg: string, kind: 'success' | 'error' | 'warn' = 'success') {
    setToast({ msg, kind });
    window.setTimeout(() => setToast(null), 4500);
  }

  return (
    <div className="app-shell admin-root">
      <div className="topbar">
        <h1>EconQuest · Admin</h1>
        <button className="ghost sm" onClick={onExitToGame} data-testid="button-exit-to-game">
          Play game
        </button>
        <button
          className="ghost sm"
          onClick={() => { window.location.href = '/review'; }}
          data-testid="button-go-review"
        >
          Open review
        </button>
        <div className="spacer" />
        <span className="who" data-testid="text-admin-name">{adminName}</span>
        <button className="ghost sm" onClick={() => void onLogout()} data-testid="button-logout">
          Sign out
        </button>
      </div>
      <div className="main-grid">
        <nav className="rail" data-testid="admin-rail">
          <div className="rail-section-title">Console</div>
          <button
            className={`nav-row ${screen === 'users' ? 'active' : ''}`}
            onClick={() => setScreen('users')}
            data-testid="nav-users"
          >
            Users
          </button>
          <button
            className={`nav-row ${screen === 'knobs' ? 'active' : ''}`}
            onClick={() => setScreen('knobs')}
            data-testid="nav-knobs"
          >
            Knobs
          </button>
          <button
            className={`nav-row ${screen === 'health' ? 'active' : ''}`}
            onClick={() => setScreen('health')}
            data-testid="nav-health"
          >
            Health
          </button>
        </nav>
        <main className="detail" data-testid="admin-detail">
          {screen === 'users' && <UsersScreen currentAdminId={adminId} showToast={showToast} />}
          {screen === 'knobs' && <KnobsScreen showToast={showToast} />}
          {screen === 'health' && <HealthScreen showToast={showToast} />}
        </main>
      </div>
      {toast && (
        <div className={`toast ${toast.kind}`} data-testid="toast" role="status">
          {toast.msg}
        </div>
      )}
    </div>
  );
}
